const app = process.env.APP_NAME

import { pathToRegexp } from 'path-to-regexp'
import {
  userIsAdmin,
  userIsCompliance,
  userHasKipRole,
  userIsLoggedIn,
  userIsVerified,
} from './router-permissions.js'

/**
 * @param {string} path
 * @returns {string}
 * */
export function getComponentName(path) {
  const trimmedPath = path.startsWith('/') ? path.slice(1) : path

  return `${trimmedPath.replace(/\//g, '-')}-container`
}
/** @param {string} path */
export function getComponentPath(path) {
  return `${path}/${getComponentName(path)}`
}

/**
 * Extracts the path from route or parent.
 * @param {Route|null} parent - The parent route object.
 * @param {Route} route - The route object to process.
 * @returns {string} - The processed path string.
 */
export function extractPath(parent, route) {
  return (parent ? `${parent.path}/${route.id}` : `/${route.id}`)
    .toLowerCase()
    .replace(/_/g, '-')
}

/**
 * Processes a single route object and adds the pathToRegexp property.
 * @param {string} key - The key of the route object.
 * @param {Route} route - The route object to process.
 * @param {Route|null} parent - The parent route object.
 * @returns {Route} - The processed route object.
 */
export function processRoute(key, route, parent = null) {
  route.id = route.id || key
  const path = route.path || extractPath(parent, route)
  const componentName = route.componentName || getComponentName(path)
  const componentPath = route.componentPath || getComponentPath(path)
  // add any parent permissions to current permissions
  route.permissions = route.permissions || []
  if (parent) route.permissions = [...parent.permissions, ...route.permissions]

  // Any sub routes should have a back arrow
  const showBackArrow = route.hasOwnProperty('showBackArrow')
    ? route.showBackArrow
    : !!parent

  const processed = {
    id: route.id,
    path,
    parent,
    pathRegexp: pathToRegexp(path),
    componentPath,
    componentName,
    showNav: route.showNav ?? false,
    hideWalletSelect: route.hideWalletSelect ?? false,
    showBackArrow,
    showHeader: route.showHeader ?? true,
    showHeaderQuickNav: route.showHeaderQuickNav ?? false,
    permissions: route.permissions ?? [],
    children: route.children ?? {},
    showMobileNav: route?.showMobileNav ?? true,
    hasToolbar: !!route.hasToolbar,
  }

  // After parent is initialized, we can process the child routes
  processed.children = processRoutes(processed.children, processed)

  return processed
}

/**
 * Processes the given route objects and adds the pathToRegexp property.
 * @param {Object<string, Route>} routes - The route objects to process.
 * @param {Route|null} parentRoute - The parent route object.
 * @returns {Object<string, Route>} - The processed route objects.
 */
function processRoutes(routes, parentRoute = null) {
  return Object.entries(routes).reduce((acc, [key, route]) => {
    acc[key] = processRoute(key, route, parentRoute)
    return acc
  }, /** @type {Object<String, Route>} */ ({}))
}

const permissions = [userIsLoggedIn]

const clientRoutes = processRoutes({
  LOGIN: {
    showHeader: false,
    showMobileNav: false,
    hideWalletSelect: true,
    children: {
      FORGOT_PASSWORD: {
        isPublic: true,
        showHeader: false,
      },
    },
  },
  SIGNUP: {
    showHeader: false,
    hideWalletSelect: true,
    showMobileNav: false,
  },
  DASHBOARD: {
    showMobileNav: true,
  },
  SELECT_WALLET: {
    permissions,
    hideWalletSelect: true,
  },
  SANDBOX: {
    showMobileNav: false,
  },
  DEV: {
    isPublic: true,
    permissions: [userIsAdmin],
  },
  REDEEM: {},
  VOTING: {
    children: {
      DETAIL: {
        hasToolbar: false,
      },
      CREATE_PROPOSAL: {
        showMobileNav: false,
      },
      PROPOSAL_ERROR: {
        showBackArrow: true,
      },
    },
    hasToolbar: false,
  },
  ADMIN: {
    permissions: [userIsAdmin],
    children: {
      PROPOSAL_COMPLIANCE: {
        permissions: [userIsCompliance],
        children: {
          DETAIL: {},
        },
      },
    },
  },
  EXECUTIVE: {
    showMobileNav: true,
    showHeader: true,
  },
  ACCOUNT: {
    showMobileNav: false,
    showBackArrow: true,
  },
  MY_ACCOUNT: {
    showMobileNav: true,
    showBackArrow: true,
  },
  PIP_REVIEW: {
    children: {
      DETAIL: {},
    },
    showBackArrow: true,
    showMobileNav: false,
    hideWalletSelect: true,
    permissions: [],
  },
  SETTINGS: {
    showMobileNav: false,
  },
  WALLET_SETTINGS: {
    showMobileNav: false,
  },
  NOT_VERIFIED: {
    showHeader: true,
    showMobileNav: false,
  },
  SUCCESS: {
    showMobileNav: false,
    showHeader: false,
  },
  KYC: {
    showMobileNav: false,
  },
  KIP: {
    showMobileNav: true,
    showHeader: true,
    permissions: [userHasKipRole],
    children: {
      DETAIL: {},
      CREATE_KIP_PROPOSAL: {
        showMobileNav: false,
      },
    },
  },
  ACTIVATION: {
    showHeader: true,
    showMobileNav: true,
  },
})

const enterpriseRoutes = processRoutes({
  LOGIN: {
    showHeader: true,
    showMobileNav: false,
  },
  SIGNUP: {
    showHeader: true,
    showMobileNav: false,
  },
  DASHBOARD: {
    showHeader: true,
    showMobileNav: true,
  },
  ACCOUNT: {
    showHeader: true,
    showMobileNav: true,
  },
  SETTINGS: {
    showHeader: true,
    showMobileNav: false,
  },
})

export default app === 'enterprise' ? enterpriseRoutes : clientRoutes
